import { useEffect, useMemo, useState } from "react";
import { useStore } from "zustand";
import { createStore } from "zustand/vanilla";
import type { GoogleCalendar, GoogleChange, GoogleChangeResult, GoogleEventsResponse, GoogleStatus, Settings } from "@otrack/domain";
import { applyPending, mergeEvents, placeChange, type GoogleEvents, type PendingChange } from "./google-events";

/**
 * Google Calendar on this device (ADR 0027). Events are cached per account and calendar and
 * kept fresh with Google's sync tokens: when the app starts, comes back into view, or goes
 * online, and every minute while it is visible. Changes apply here at once and wait in a queue
 * until Google takes them, so they work offline too.
 */

export interface GoogleApi {
  status(): Promise<GoogleStatus>;
  calendars(): Promise<GoogleCalendar[]>;
  events(calendarId: string, syncToken: string | null): Promise<GoogleEventsResponse>;
  apply(changes: GoogleChange[]): Promise<GoogleChangeResult[]>;
  disconnect(): Promise<void>;
}

/** What the web and desktop apps provide for Google Calendar; absent when ontrack has no server. */
export interface GoogleServices {
  api: GoogleApi;
  /** Opens Google's consent for calendar access on the signed-in Google account. */
  connect: () => Promise<void> | void;
  /** Where this account keeps its Google cache and queued changes on this device. */
  storage: { get(key: string): Promise<unknown>; set(key: string, value: unknown): Promise<void> };
}

/** An API failure, with the Worker's error code when there is one. */
export class GoogleRequestError extends Error {
  constructor(message: string, readonly code: string | null) {
    super(message);
  }
}

export type GooglePhase = "loading" | "connected" | "disconnected" | "reconnect" | "unavailable";

export interface GoogleState {
  phase: GooglePhase;
  email: string | null;
  calendars: GoogleCalendar[];
  /** Events as Google last sent them. */
  server: GoogleEvents;
  /** Changes waiting for Google, oldest first. */
  pending: PendingChange[];
  /** What the Calendar shows: `server` with `pending` applied. */
  events: GoogleEvents;
  syncing: boolean;
  syncedAt: number | null;
  /** Why the last sync didn't finish, when it didn't. */
  error: string | null;
}

interface Saved {
  version: 1;
  phase: GooglePhase;
  email: string | null;
  calendars: GoogleCalendar[];
  server: GoogleEvents;
  tokens: Record<string, string>;
  pending: PendingChange[];
}

const STORAGE_KEY = "google-calendar";
const CALENDARS_EVERY = 10 * 60_000;
const BATCH = 25;

const initial: GoogleState = { phase: "loading", email: null, calendars: [], server: {}, pending: [], events: {}, syncing: false, syncedAt: null, error: null };

/** Calendars switched on in Settings; one never switched shows only when it is the primary one. */
export function enabledCalendars(calendars: readonly GoogleCalendar[], selection: Readonly<Record<string, boolean>>): GoogleCalendar[] {
  return calendars.filter((calendar) => selection[calendar.id] ?? calendar.primary);
}

/** Calendars ontrack can add events to. */
export function writableCalendars(calendars: readonly GoogleCalendar[]): GoogleCalendar[] {
  return calendars.filter((calendar) => calendar.accessRole === "owner" || calendar.accessRole === "writer");
}

function phaseOf(cause: unknown): GooglePhase | null {
  const code = cause instanceof GoogleRequestError ? cause.code : null;
  if (code === "google_reconnect") return "reconnect";
  if (code === "google_disconnected") return "disconnected";
  if (code === "google_unavailable" || code === "sign_in_required") return "unavailable";
  return null;
}

const messageOf = (cause: unknown) => (cause instanceof Error ? cause.message : "Google Calendar could not be reached");

export class GoogleCalendarSync {
  readonly store = createStore<GoogleState>()(() => initial);
  private tokens: Record<string, string> = {};
  private selection: Record<string, boolean> = {};
  private running: Promise<void> | null = null;
  private again = false;
  private statusKnown = false;
  private calendarsAt = 0;
  private saveTimer: ReturnType<typeof setTimeout> | undefined;
  private notice: (message: string) => void = () => {};

  constructor(private readonly services: GoogleServices) {}

  /** Reads this account's cache. Nothing is fetched until `sync`. */
  async load(): Promise<void> {
    const saved = await this.services.storage.get(STORAGE_KEY).catch(() => null) as Saved | null;
    if (!saved || saved.version !== 1) return;
    this.tokens = saved.tokens ?? {};
    this.set({ phase: saved.phase, email: saved.email, calendars: saved.calendars ?? [], server: saved.server ?? {}, pending: saved.pending ?? [] });
  }

  onNotice(listener: (message: string) => void): void {
    this.notice = listener;
  }

  /** Follows Settings: switching a calendar on fetches it. */
  setSelection(selection: Record<string, boolean>): void {
    const before = this.enabledIds();
    this.selection = selection;
    if (this.enabledIds().some((id) => !before.includes(id))) void this.sync();
  }

  connect(): void {
    void Promise.resolve().then(() => this.services.connect()).catch((cause: unknown) => this.notice(messageOf(cause)));
  }

  /** Applies changes here at once and queues them for Google. */
  enqueue(changes: readonly PendingChange[]): void {
    if (!changes.length) return;
    this.set({ pending: [...this.store.getState().pending, ...changes] });
    void this.sync({ pullAfter: false });
  }

  /**
   * Sends queued changes, then fetches what changed in Google. Calls while one runs wait for it
   * and run once more, so nothing is missed and requests never overlap.
   */
  sync(options: { force?: boolean; pullAfter?: boolean } = {}): Promise<void> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = (async () => {
      try {
        do {
          this.again = false;
          await this.run(options.force ?? false, options.pullAfter ?? true);
        } while (this.again);
      } finally {
        this.running = null;
      }
    })();
    return this.running;
  }

  /** Revokes ontrack's access and forgets everything Google on this device, unsent changes too. */
  async disconnect(): Promise<void> {
    await this.services.api.disconnect();
    this.tokens = {};
    this.statusKnown = true;
    this.set({ ...initial, phase: "disconnected" });
  }

  private enabledIds(): string[] {
    return enabledCalendars(this.store.getState().calendars, this.selection).map((calendar) => calendar.id);
  }

  private set(patch: Partial<GoogleState>): void {
    const next = { ...this.store.getState(), ...patch };
    if (patch.server || patch.pending) next.events = applyPending(next.server, next.pending);
    this.store.setState(next);
    if (patch.server || patch.pending || patch.calendars || patch.phase || patch.email !== undefined) this.save();
  }

  private save(): void {
    clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      const { phase, email, calendars, server, pending } = this.store.getState();
      const saved: Saved = { version: 1, phase, email, calendars, server, tokens: this.tokens, pending };
      void this.services.storage.set(STORAGE_KEY, saved).catch(() => undefined);
    }, 400);
  }

  private async run(force: boolean, pull: boolean): Promise<void> {
    const { api } = this.services;
    this.set({ syncing: true });
    try {
      if (!this.statusKnown || force || this.store.getState().phase !== "connected") {
        const status = await api.status();
        this.statusKnown = true;
        this.set({ phase: status.state, email: status.email ?? this.store.getState().email });
        if (status.state !== "connected") return;
      }
      await this.flush();
      if (!pull && !force) return;
      if (force || Date.now() - this.calendarsAt > CALENDARS_EVERY || !this.store.getState().calendars.length) {
        this.set({ calendars: await api.calendars() });
        this.calendarsAt = Date.now();
      }
      for (const id of this.enabledIds()) await this.pull(id);
      this.set({ syncedAt: Date.now(), error: null });
    } catch (cause) {
      const phase = phaseOf(cause);
      if (phase) this.set({ phase, error: null });
      else this.set({ error: messageOf(cause) });
    } finally {
      this.set({ syncing: false });
    }
  }

  private async pull(calendarId: string): Promise<void> {
    const token = this.tokens[calendarId] ?? null;
    let response = await this.services.api.events(calendarId, token);
    if (response.reset) {
      delete this.tokens[calendarId];
      response = await this.services.api.events(calendarId, null);
    }
    const full = token === null || response.reset;
    const server = { ...this.store.getState().server, [calendarId]: mergeEvents(this.store.getState().server[calendarId], response.events, full) };
    if (response.syncToken) this.tokens[calendarId] = response.syncToken;
    else delete this.tokens[calendarId];
    this.set({ server });
  }

  /** Sends queued changes in order. Gone and refused ones are dropped with a notice; the rest wait. */
  private async flush(): Promise<void> {
    for (;;) {
      const batch = this.store.getState().pending.slice(0, BATCH);
      if (!batch.length) return;
      const results = await this.services.api.apply(batch.map((item) => item.change));
      const server = { ...this.store.getState().server };
      const done = new Set<string>();
      let stop = results.length < batch.length;
      results.forEach((result, index) => {
        const item = batch[index]!;
        if (result.ok) {
          placeChange(server, item.change, result.event ?? item.local);
          done.add(item.id);
          return;
        }
        if (result.reason === "gone") {
          done.add(item.id);
          this.notice(`"${item.label}" was deleted in Google, so that change was dropped.`);
          return;
        }
        if (result.reason === "refused") {
          done.add(item.id);
          this.notice(`Google didn't take the change to "${item.label}": ${result.message}`);
          return;
        }
        stop = true;
        if (result.reason === "reconnect") this.set({ phase: "reconnect" });
        else this.set({ error: result.message });
      });
      this.set({ server, pending: this.store.getState().pending.filter((item) => !done.has(item.id)) });
      if (stop) return;
    }
  }
}

/**
 * Runs Google Calendar sync for the app: the cache loads, then a sync runs now, every minute
 * while the app is visible, and whenever it comes back into view or online.
 */
export function useGoogleSync(services: GoogleServices | null | undefined, settings: Settings, notify: (message: string) => void): GoogleCalendarSync | null {
  const [sync, setSync] = useState<GoogleCalendarSync | null>(null);

  useEffect(() => {
    if (!services) return;
    const current = new GoogleCalendarSync(services);
    let alive = true;
    let last = 0;
    const tick = (minimumGap: number) => {
      if (!alive || document.visibilityState !== "visible" || Date.now() - last < minimumGap) return;
      last = Date.now();
      void current.sync();
    };
    const onFocus = () => tick(15_000);
    void current.load().then(() => {
      if (!alive) return;
      setSync(current);
      tick(0);
    });
    const timer = window.setInterval(() => tick(50_000), 60_000);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      alive = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      setSync(null);
    };
  }, [services]);

  useEffect(() => { sync?.onNotice(notify); }, [sync, notify]);
  useEffect(() => { sync?.setSelection(settings.googleCalendars); }, [sync, settings.googleCalendars]);
  return sync;
}

const EMPTY = createStore<GoogleState>()(() => ({ ...initial, phase: "unavailable" }));

/** Google Calendar's state on this device; "unavailable" where ontrack has no server. */
export function useGoogleState(sync: GoogleCalendarSync | null): GoogleState {
  return useStore(sync?.store ?? EMPTY);
}

/** What the Calendar and Today draw from Google: switched-on calendars and their events. */
export interface GoogleView {
  calendars: GoogleCalendar[];
  events: GoogleEvents;
}

export function useGoogleView(sync: GoogleCalendarSync | null, settings: Settings): GoogleView {
  const state = useGoogleState(sync);
  const connected = state.phase === "connected" || state.phase === "reconnect";
  return useMemo(() => ({
    calendars: connected ? enabledCalendars(state.calendars, settings.googleCalendars) : [],
    events: state.events
  }), [connected, state.calendars, state.events, settings.googleCalendars]);
}
